/**
 * Consent management abstraction — CookieHub only.
 */

declare global {
  interface Window {
    cookiehub?: {
      hasConsented: (category: string) => boolean
      hasAnswered: () => boolean
      openSettings: (tab?: string) => void
      allowAll: () => void
      denyAll: () => void
    }
  }
}

// CookieHub defers `load({})` to DOMContentLoaded and the visitor still has to answer the banner, so
// an early caller reaches `hasMarketingConsent()` before `window.cookiehub` exists and reads a bare
// `false`. Long enough for the script plus a banner click, short enough to give up while the page is open.
const CONSENT_WAIT_TIMEOUT_MS = 15_000
const CONSENT_POLL_INTERVAL_MS = 200
// CookieHub's own event, also used in `_app.tsx` to release held Optimizely events.
const COOKIEHUB_STATUS_CHANGE_EVENT = 'cookiehub_onStatusChange'

const hasAnsweredConsent = (): boolean => {
  const cookiehub = window.cookiehub
  if (!cookiehub) return false
  return cookiehub.hasAnswered()
}

export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false

  if (window.cookiehub) {
    return window.cookiehub.hasConsented('analytics')
  }

  return false
}

export function hasMarketingConsent(): boolean {
  if (typeof window === 'undefined') return false

  if (window.cookiehub) {
    return window.cookiehub.hasConsented('marketing')
  }

  return false
}

/**
 * Resolves with `hasMarketingConsent()` once the choice is known, or `false` on timeout — an
 * unanswered banner is not consent.
 */
export function whenMarketingConsentKnown(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (hasAnsweredConsent()) return Promise.resolve(hasMarketingConsent())

  return new Promise((resolve) => {
    let settled = false

    const finish = (consented: boolean) => {
      if (settled) return
      settled = true
      window.clearInterval(pollId)
      window.clearTimeout(timeoutId)
      document.removeEventListener(COOKIEHUB_STATUS_CHANGE_EVENT, onStatusChange)
      resolve(consented)
    }

    const checkNow = () => {
      if (hasAnsweredConsent()) finish(hasMarketingConsent())
    }

    // Event covers answering the banner; poll covers a returning visitor whose stored choice fires none.
    const onStatusChange = () => checkNow()
    document.addEventListener(COOKIEHUB_STATUS_CHANGE_EVENT, onStatusChange)
    const pollId = window.setInterval(checkNow, CONSENT_POLL_INTERVAL_MS)
    const timeoutId = window.setTimeout(() => finish(false), CONSENT_WAIT_TIMEOUT_MS)

    checkNow()
  })
}

/**
 * Marketing consent as a tri-state, where `undefined` means "not known yet" —
 * CookieHub loads asynchronously and only settles once the user has answered the
 * banner.
 */
export function getMarketingConsent(): boolean | undefined {
  if (typeof window === 'undefined' || !window.cookiehub) return undefined

  if (window.cookiehub.hasConsented('marketing')) return true

  return window.cookiehub.hasAnswered() ? false : undefined
}

export function openCookiePreferences(): void {
  if (typeof window === 'undefined') return

  if (window.cookiehub) {
    window.cookiehub.openSettings()
  }
}
